import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { useSupabase } from '@t90/api-client';

interface AppUserProfile {
  id: string;
  email: string;
  full_name?: string;
  avatar_url?: string;
  location_id?: string;
  role?: string;
  [key: string]: any;
}

interface AuthContextType {
  user: AppUserProfile | null;
  session: Session | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const supabase = useSupabase();
  const [user, setUser] = useState<AppUserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchProfile(supabaseUser: SupabaseUser) {
      const { data, error } = await supabase
        .from('app_users')
        .select('*')
        .eq('id', supabaseUser.id)
        .maybeSingle();
      
      if (error || !data) {
        // User doesn't exist in app_users, create a basic profile
        console.log('User not found in app_users, creating profile...', { userId: supabaseUser.id, email: supabaseUser.email });
        
        const fullName = supabaseUser.user_metadata?.full_name || 
                        supabaseUser.user_metadata?.name || 
                        supabaseUser.email?.split('@')[0] || 
                        'Unknown User';
        
        const newUserData = {
          id: supabaseUser.id,
          email: supabaseUser.email!,
          full_name: fullName,
          role: 'Ops Manager', // Default role - adjust as needed
          active: true,
        };
        
        console.log('Attempting to insert user:', newUserData);
        
        const { data: newUser, error: insertError } = await supabase
          .from('app_users')
          .insert(newUserData)
          .select()
          .single();
        
        if (insertError) {
          console.error('❌ Failed to create user profile:', insertError);
          console.error('Insert error details:', {
            code: insertError.code,
            message: insertError.message,
            details: insertError.details,
            hint: insertError.hint
          });
          setUser({
            id: supabaseUser.id,
            email: supabaseUser.email ?? '',
          });
        } else {
          console.log('✅ Successfully created user profile:', newUser);
          setUser({ id: supabaseUser.id, email: supabaseUser.email ?? '', ...(newUser as any) });
        }
      } else {
        console.log('User profile found:', data);
        setUser({ id: supabaseUser.id, email: supabaseUser.email ?? '', ...(data as any) });
      }
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        fetchProfile(session.user);
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        if (session?.user) {
          fetchProfile(session.user);
        } else {
          setUser(null);
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, [supabase]);

  async function signInWithGoogle() {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });

    if (error) {
      throw error;
    }
  }

  async function signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
    setUser(null);
    setSession(null);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signInWithGoogle,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
